import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import config from '../config.json';

import {
  loadProvider,
  loadNetwork,
  loadAccount,
  loadTokens,
  loadExchange,
  loadVault,
  loadMgtvToken,
  subscribeToEvents,
  loadAllOrders
} from '../store/interactions';

import Landing from './Landing'
import Navbar from './Navbar'
import ProfileDropdown from './ProfileDropdown'
import Orderbook from './Orderbook'
import PriceChart from './PriceChart'
import Trades from './Trades'
import MyFilledOrders from './MyFilledOrders'
import Alert from './Alert'
import InstructionsPage from './InstructionsPage'
import DocsPage from './DocsPage'
import AboutPage from './AboutPage'
import ContactPage from './ContactPage'
import WhitepaperPage from './WhitepaperPage'
import greywolfLogo from '../assets/greywolf.png'


function App() {
  const dispatch = useDispatch()
  const [showLanding, setShowLanding] = useState(true)
  const [panelVisibility, setPanelVisibility] = useState({
    orderbook: false,
    trades: false,
    myTransactions: false
  })
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const [activePage, setActivePage] = useState('dex')
  const account = useSelector(state => state.provider.account)
  const prevAccount = useRef(null)

  useEffect(() => {
    if (account && !prevAccount.current) {
      setProfileDropdownOpen(true)
    }
    prevAccount.current = account
  }, [account])

  const loadBlockchainData = async () => {
    const provider = loadProvider(dispatch)
    const chainId = await loadNetwork(provider, dispatch)

    window.ethereum.on('chainChanged', () => {
      window.location.reload()
    })

    window.ethereum.on('accountsChanged', () => {
      loadAccount(provider, dispatch)
    })

    // Load MG/USDT as the trading pair
    const MG = config[chainId].MG
    const USDT = config[chainId].USDT
    await loadTokens(provider, [MG.address, USDT.address], dispatch)

    // Load exchange
    const exchangeConfig = config[chainId].exchange
    const exchange = await loadExchange(provider, exchangeConfig.address, dispatch)

    // Load Gold Vault
    const vaultConfig = config[chainId].goldVault
    if (vaultConfig && vaultConfig.address) {
      await loadVault(provider, vaultConfig.address, dispatch)
    }

    // Load MGTV token
    const mgtvConfig = config[chainId].MGTV
    if (mgtvConfig && mgtvConfig.address) {
      await loadMgtvToken(provider, mgtvConfig.address, dispatch)
    }

    loadAllOrders(provider, exchange, dispatch)
    subscribeToEvents(exchange, dispatch)
  }

  useEffect(() => {
    loadBlockchainData()
  })

  const togglePanel = (panel) => {
    setPanelVisibility(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }))
  }



  if (showLanding) {
    return <Landing onEnter={() => setShowLanding(false)} />
  }

  return (
    <div>

      <Navbar
        onMenuClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
        activePage={activePage}
        onPageChange={setActivePage}
      />

      <ProfileDropdown
        isOpen={profileDropdownOpen}
        onClose={() => setProfileDropdownOpen(false)}
      />

      {activePage === 'dex' && (
        <main className='exchange__main'>

          <PriceChart />

          <div className='exchange__panels'>

              <div className={`panel-collapse ${panelVisibility.orderbook ? 'panel-collapse--open' : ''}`}>
                <button className='panel-collapse__header' onClick={() => togglePanel('orderbook')}>
                  <span>Order Book</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`panel-collapse__icon ${panelVisibility.orderbook ? 'panel-collapse__icon--open' : ''}`}>
                    <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {panelVisibility.orderbook && <Orderbook />}
              </div>

              <div className={`panel-collapse ${panelVisibility.trades ? 'panel-collapse--open' : ''}`}>
                <button className='panel-collapse__header' onClick={() => togglePanel('trades')}>
                  <span>Trades</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`panel-collapse__icon ${panelVisibility.trades ? 'panel-collapse__icon--open' : ''}`}>
                    <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {panelVisibility.trades && <Trades />}
              </div>

              <div className={`panel-collapse ${panelVisibility.myTransactions ? 'panel-collapse--open' : ''}`}>
                <button className='panel-collapse__header' onClick={() => togglePanel('myTransactions')}>
                  <span>My Transactions</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`panel-collapse__icon ${panelVisibility.myTransactions ? 'panel-collapse__icon--open' : ''}`}>
                    <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {panelVisibility.myTransactions && <MyFilledOrders />}
              </div>

            </div>

        </main>
      )}

      {activePage === 'instructions' && (
        <main className='exchange__main'>
          <InstructionsPage />
        </main>
      )}

      {activePage === 'docs' && (
        <main className='exchange__main'>
          <DocsPage />
        </main>
      )}

      {activePage === 'about' && (
        <main className='exchange__main'>
          <AboutPage />
        </main>
      )}

      {activePage === 'contact' && (
        <main className='exchange__main'>
          <ContactPage />
        </main>
      )}

      {activePage === 'whitepaper' && (
        <main className='exchange__main'>
          <WhitepaperPage />
        </main>
      )}

      <Alert />

      <footer className='exchange__footer'>
        <div className='exchange__footer--left'>
          <p>milligram.tv &mdash; Trade Gold. Anytime.</p>
          <small>&copy; 2026 milligram.tv. All rights reserved.</small>
        </div>
        <div className='exchange__footer--center'>
          <span>Made by</span>
          <a href='https://greywolf.build' target='_blank' rel='noreferrer'>
            <img src={greywolfLogo} alt="greywolf" className="exchange__footer-gw" />
            greywolf
          </a>
        </div>
        <div className='exchange__footer--right'>
          <span>Powered by</span>
          <a href='https://ethereum.org/' target='_blank' rel='noreferrer'>
            <svg width="20" height="20" viewBox="0 0 256 417" xmlns="http://www.w3.org/2000/svg">
              <path fill="#343434" d="M127.961 0l-2.795 9.5v275.668l2.795 2.79 127.962-75.638z"/>
              <path fill="#8C8C8C" d="M127.962 0L0 212.32l127.962 75.639V154.158z"/>
              <path fill="#3C3C3B" d="M127.961 312.187l-1.575 1.92v98.199l1.575 4.6L256 236.587z"/>
              <path fill="#8C8C8C" d="M127.962 416.905v-104.72L0 236.585z"/>
              <path fill="#141414" d="M127.961 287.958l127.96-75.637-127.96-58.162z"/>
              <path fill="#393939" d="M0 212.32l127.96 75.638v-133.8z"/>
            </svg>
            Ethereum
          </a>
        </div>
      </footer>

    </div>
  );
}

export default App;
